import { type ReactNode } from 'react';

import { Tooltip, TooltipContent, TooltipTrigger } from 'cpool-components/general/Tooltip';
import { TooltipVariant } from 'cpool-components/general/Tooltip/types';
import { type ApprovedWhatsAppTemplateParameters, WHATSAPP_TEMPLATE_VARIABLE_SOURCES } from 'cpool-constants';
import { useTranslation } from 'react-i18next';
import { LuX } from 'react-icons/lu';

import { AutosizeChipInput } from './AutosizeChipInput';
import { PositionalChip } from './PositionalChip';
import { getEmptyChipHint } from './utils/getEmptyChipHint';
import { TemplateButtonsRow } from '~/features/whatsapp/components/MessageStream/TemplateButtonsRow';
import { WhatsAppFormattedText } from '~/features/whatsapp/components/WhatsAppFormattedText';
import {
    TEMPLATE_COMPOSER_MAX_HEIGHT_PX,
    TEMPLATE_COMPOSER_MIN_HEIGHT_PX,
} from '~/features/whatsapp/constants/templatePicker';
import type { WhatsAppTemplate } from '~/features/whatsapp/hooks/useWhatsAppTemplates';
import {
    parseTemplateSegments,
    type TemplateSegment,
    WHATSAPP_TEMPLATE_SEGMENT_KINDS,
} from '~/features/whatsapp/utils/parseTemplateSegments';
import { type TemplatePreviewSourceValues } from '~/features/whatsapp/utils/substituteTemplatePlaceholders';
import { cn } from '~/utils/cnTailwind';

type Props = {
    template: WhatsAppTemplate | undefined;
    templateLanguage: string | undefined;
    namedValues: Record<string, string>;
    // HEADER named-slot values — a name shared with the body's namedValues can hold a different value.
    headerNamedValues: Record<string, string>;
    positionalValues: Array<string>;
    headerPositionalValues: Array<string>;
    onNamedValueChange: (name: string, value: string) => void;
    onHeaderNamedValueChange: (name: string, value: string) => void;
    onPositionalValueChange: (index: number, value: string) => void;
    onHeaderPositionalValueChange: (index: number, value: string) => void;
    previewValues: TemplatePreviewSourceValues;
    text: string;
    onTextChange: (value: string) => void;
    onFocus: () => void;
    onEnterSend: () => void;
    onClearTemplate: () => void;
    disabled: boolean;
    placeholder: string;
};

const COMPOSER_HEIGHT_STYLE = {
    maxHeight: TEMPLATE_COMPOSER_MAX_HEIGHT_PX,
    minHeight: TEMPLATE_COMPOSER_MIN_HEIGHT_PX,
};

type SegmentSlotConfig = {
    namedValues: Record<string, string>;
    onNamedValueChange: (name: string, value: string) => void;
    positionalLabelKey: Parameters<ReturnType<typeof useTranslation>['t']>[0];
    onPositionalChange: (index: number, value: string) => void;
    positionalValues: Array<string>;
};

export const ChipComposerInput = ({
    disabled,
    headerNamedValues,
    headerPositionalValues,
    namedValues,
    onClearTemplate,
    onEnterSend,
    onFocus,
    onHeaderNamedValueChange,
    onHeaderPositionalValueChange,
    onNamedValueChange,
    onPositionalValueChange,
    onTextChange,
    placeholder,
    positionalValues,
    previewValues,
    template,
    templateLanguage,
    text,
}: Props) => {
    const { t } = useTranslation();

    if (!template) {
        return (
            <textarea
                value={text}
                onChange={(e) => onTextChange(e.target.value)}
                onFocus={onFocus}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        onEnterSend();
                    }
                }}
                placeholder={placeholder}
                disabled={disabled}
                rows={1}
                style={COMPOSER_HEIGHT_STYLE}
                className={cn(
                    'flex-1 resize-none rounded-full border border-outline-light px-3 py-2 text-sm disabled:overflow-hidden disabled:whitespace-nowrap disabled:bg-surface-light disabled:leading-normal disabled:text-grey',
                    !disabled && 'overflow-y-auto',
                )}
            />
        );
    }

    const language = templateLanguage ?? template.languages[0];
    const bodyText = (language && template.body[language]) || '';

    const { headerParameters } = template;
    const headerText = (language && template.header?.[language]) || '';
    const buttons = (language && template.buttons?.[language]) || [];

    const renderSegment = (segment: TemplateSegment, index: number, slot: SegmentSlotConfig): ReactNode => {
        if (segment.kind === WHATSAPP_TEMPLATE_SEGMENT_KINDS.TEXT) {
            return <span key={index}>{segment.text}</span>;
        }

        if (segment.kind === WHATSAPP_TEMPLATE_SEGMENT_KINDS.POSITIONAL_PLACEHOLDER) {
            return (
                <PositionalChip
                    key={index}
                    label={t(slot.positionalLabelKey, { index: segment.index })}
                    value={slot.positionalValues[segment.index - 1] ?? ''}
                    onChange={(next) => slot.onPositionalChange(segment.index - 1, next)}
                />
            );
        }

        const label = t(`WHATSAPP_CHAT.TEMPLATE_MODAL.VARIABLES.${segment.name}`, {
            defaultValue: segment.name,
        });

        // resolved at send time — falling through would flag it as a missing value
        if (segment.variable.source === WHATSAPP_TEMPLATE_VARIABLE_SOURCES.COMPUTED) {
            return (
                <Tooltip key={index}>
                    <TooltipTrigger asChild>
                        <span
                            aria-label={label}
                            className="rounded-md border border-outline-light bg-surface-light px-1 py-px text-midnight"
                        >
                            {label}
                        </span>
                    </TooltipTrigger>
                    <TooltipContent hideArrow side="top" variant={TooltipVariant.LIGHT}>
                        {t('WHATSAPP_CHAT.TEMPLATE_MODAL.COMPUTED_HINT')}
                    </TooltipContent>
                </Tooltip>
            );
        }

        const isCustomVariable = segment.variable.source === WHATSAPP_TEMPLATE_VARIABLE_SOURCES.CUSTOM;
        const resolvedValue = isCustomVariable
            ? undefined
            : previewValues[segment.name as keyof TemplatePreviewSourceValues];
        const value = slot.namedValues[segment.name] ?? resolvedValue ?? '';
        const hint = value ? undefined : getEmptyChipHint(t, label, resolvedValue);

        return (
            <AutosizeChipInput
                key={index}
                ariaLabel={label}
                value={value}
                placeholder={hint?.text}
                placeholderVariant={hint?.variant}
                onChange={(next) => slot.onNamedValueChange(segment.name, next)}
            />
        );
    };

    // Formatting parses the whole text first so markers spanning a placeholder (`*Hi {{name}}*`) still apply.
    const renderFormattedTemplate = (
        templateText: string,
        parameters: ApprovedWhatsAppTemplateParameters,
        slot: SegmentSlotConfig,
    ): ReactNode => (
        <WhatsAppFormattedText
            text={templateText}
            renderText={(value) =>
                parseTemplateSegments(value, parameters).map((segment, index) => renderSegment(segment, index, slot))
            }
        />
    );

    return (
        <div
            data-testid="chip-composer-input"
            style={COMPOSER_HEIGHT_STYLE}
            className="flex flex-1 flex-col overflow-y-auto rounded-md border border-outline-light text-sm leading-5"
        >
            <div className="flex items-start">
                <div className="min-w-0 flex-1 whitespace-pre-wrap px-3 py-2 text-midnight">
                    {headerText && headerParameters && (
                        <div data-testid="chip-composer-header" className="mb-1 font-semibold">
                            {renderFormattedTemplate(headerText, headerParameters, {
                                namedValues: headerNamedValues,
                                onNamedValueChange: onHeaderNamedValueChange,
                                onPositionalChange: onHeaderPositionalValueChange,
                                positionalLabelKey: 'WHATSAPP_CHAT.TEMPLATE_MODAL.HEADER_POSITIONAL_SLOT',
                                positionalValues: headerPositionalValues,
                            })}
                        </div>
                    )}
                    {renderFormattedTemplate(bodyText, template.parameters, {
                        namedValues,
                        onNamedValueChange,
                        onPositionalChange: onPositionalValueChange,
                        positionalLabelKey: 'WHATSAPP_CHAT.TEMPLATE_MODAL.POSITIONAL_SLOT',
                        positionalValues,
                    })}
                </div>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <button
                            type="button"
                            aria-label={t('WHATSAPP_CHAT.COMPOSER.CLEAR_TEMPLATE')}
                            className="shrink-0 p-1.5 text-grey hover:text-midnight"
                            onClick={onClearTemplate}
                        >
                            <LuX className="size-4" />
                        </button>
                    </TooltipTrigger>
                    <TooltipContent hideArrow side="top" variant={TooltipVariant.LIGHT}>
                        {t('WHATSAPP_CHAT.COMPOSER.CLEAR_TEMPLATE')}
                    </TooltipContent>
                </Tooltip>
            </div>
            {buttons.length > 0 && <TemplateButtonsRow buttons={buttons} className="m-0 bg-grey-table-hover" />}
        </div>
    );
};
